// Animates Turbo Drive page swaps with the View Transitions API. Browsers
// without it (or with reduced motion on) keep Turbo's instant swap.
// Frames and streams (modals, drawers) don't fire turbo:before-render, except
// that a frame with data-turbo-action="advance" follows its own render with a
// page visit that only updates the URL; that render is skipped too.

const renderTimeout = 1000

function prefersReducedMotion () {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// Visiting a cached page renders twice: Turbo's cached preview, then the fresh
// copy from the server. Only the preview animates; the fresh copy replaces a
// page that's already on screen, so animating it again replays the motion.
let previewRendered = false

// Swapping the page while a transition runs cancels it mid-motion, so a fresh
// copy that arrives early waits for the preview's transition to end.
let activeTransition = null

// After a frame with data-turbo-action loads, Turbo makes a visit that only
// updates the URL. Turbo 7.3 starts it inside turbo:frame-load, Turbo 8 a few
// milliseconds later (after re-reading the response), so the next visit is
// taken to be it, unless none comes within urlOnlyVisitWindow.
const urlOnlyVisitWindow = 1000
let expectUrlOnlyVisit = false
let expectUrlOnlyVisitTimer = null
let urlOnlyVisit = false

function frameLoadListener (event) {
  if (!event.target.hasAttribute('data-turbo-action')) return

  expectUrlOnlyVisit = true
  clearTimeout(expectUrlOnlyVisitTimer)
  expectUrlOnlyVisitTimer = setTimeout(() => { expectUrlOnlyVisit = false }, urlOnlyVisitWindow)
}

function visitListener () {
  previewRendered = false
  urlOnlyVisit = expectUrlOnlyVisit
  expectUrlOnlyVisit = false
}

function beforeRenderListener (event) {
  const root = document.documentElement

  if (urlOnlyVisit) {
    urlOnlyVisit = false
    return
  }

  // Turbo marks <html> with data-turbo-preview for the render about to happen.
  if (root.hasAttribute('data-turbo-preview')) {
    previewRendered = true
  } else if (previewRendered) {
    previewRendered = false

    if (activeTransition) {
      event.preventDefault()

      const resume = () => event.detail.resume()
      activeTransition.finished.then(resume, resume)
    }

    return
  }

  if (!document.startViewTransition || prefersReducedMotion()) {
    return
  }

  event.preventDefault()

  // Named elements (navbar, tabs) only get their view-transition-name while a
  // transition runs: a name makes the element its own stacking context, which
  // would trap its dropdowns under later page content.
  root.classList.add('page-transition-active')

  const transition = document.startViewTransition(() => new Promise((resolve) => {
    // Resolve on a timer too, so a failed render can't freeze the page under
    // the transition overlay.
    const timer = setTimeout(resolve, renderTimeout)

    document.addEventListener('turbo:render', () => {
      clearTimeout(timer)
      resolve()
    }, { once: true })

    event.detail.resume()
  }))

  activeTransition = transition

  const cleanUp = () => {
    root.classList.remove('page-transition-active')

    if (activeTransition === transition) {
      activeTransition = null
    }
  }

  transition.finished.then(cleanUp, cleanUp)
}

function start () {
  document.addEventListener('turbo:frame-load', frameLoadListener)
  document.addEventListener('turbo:visit', visitListener)
  document.addEventListener('turbo:before-render', beforeRenderListener)
}

export { start }
